import { subMinutes } from 'date-fns'
import cloneDeep from 'lodash-es/cloneDeep'
import isNil from 'lodash-es/isNil'
import omit from 'lodash-es/omit'
import pick from 'lodash-es/pick'
import uniqueId from 'lodash-es/uniqueId'

import { EMPTY_OBJECT } from '../../constants/common'
import { createDate, toUkTimezoneDate } from '../../utils/date'

import {
  APPOINTMENT_HISTORY_FIRST_OFFSET,
  APPOINTMENT_HISTORY_PAGE_SIZE,
  APPOINTMENT_MOCK_FOLLOW_UP_FLOOR_MINUTE,
  APPOINTMENT_MOCK_FOLLOW_UP_GUARD_MINUTES,
  APPOINTMENT_MOCK_FOLLOW_UP_HOUR,
  APPOINTMENT_MOCK_INTERNAL_FIELDS,
  APPOINTMENT_MOCK_REQUEST_SUMMARY_FIELDS,
  AppointmentFollowUpPromptKind,
  AppointmentHistoryAction,
  AppointmentHistoryLinkKind,
} from './Appointment.constants'

export function getAppointmentMockId() {
  return Number(uniqueId())
}

export function getAppointmentMockError({ data = EMPTY_OBJECT, status }) {
  const error = new Error(`Request failed with status code ${status}`)

  error.response = { data, status }

  return error
}

export function getIsAppointmentMockSameInstant(value, otherValue) {
  return isNil(value) || isNil(otherValue)
    ? isNil(value) && isNil(otherValue)
    : new Date(value).getTime() === new Date(otherValue).getTime()
}

export function getIsAppointmentMockTimeValid({ endAt, startAt }) {
  return new Date(endAt).getTime() > new Date(startAt).getTime()
}

export function getAppointmentMockTargetDueDate(record) {
  const day = toUkTimezoneDate(record.start_at, record.timezone)

  const dayArgs = [day.getFullYear(), day.getMonth(), day.getDate()]

  const morning = createDate(
    record.timezone,
    ...dayArgs,
    APPOINTMENT_MOCK_FOLLOW_UP_HOUR
  ).getTime()

  const floor = createDate(
    record.timezone,
    ...dayArgs,
    0,
    APPOINTMENT_MOCK_FOLLOW_UP_FLOOR_MINUTE
  ).getTime()

  const guard = subMinutes(
    new Date(record.start_at),
    APPOINTMENT_MOCK_FOLLOW_UP_GUARD_MINUTES
  ).getTime()

  const target = record.is_all_day
    ? morning
    : Math.max(Math.min(morning, guard), floor)

  return new Date(target).toISOString()
}

export function getIsAppointmentMockFollowUpLive(record) {
  return (
    record.follow_up.coupled &&
    getIsAppointmentMockSameInstant(
      record.requestDueDate,
      record.follow_up.written_value
    )
  )
}

export function getAppointmentMockFollowUpPrompt({ kind, record }) {
  const { request } = record.anchor

  const targetDueDate = getAppointmentMockTargetDueDate(record)

  return request?.can_edit_due_date &&
    !getIsAppointmentMockSameInstant(record.requestDueDate, targetDueDate)
    ? {
        current_due_date: record.requestDueDate,
        day_load_advisory: null,
        kind,
        request: pick(request, APPOINTMENT_MOCK_REQUEST_SUMMARY_FIELDS),
        restore_job_enabled: record.restoreJobEnabled,
        target_due_date: targetDueDate,
      }
    : null
}

export function getAppointmentMockUpdatePrompt({
  data,
  isFollowUpLive,
  isRescheduled,
  record,
}) {
  if (!isNil(data.request)) {
    return getAppointmentMockFollowUpPrompt({
      kind: AppointmentFollowUpPromptKind.create,
      record,
    })
  }

  const prompt =
    isRescheduled && isFollowUpLive
      ? getAppointmentMockFollowUpPrompt({
          kind: AppointmentFollowUpPromptKind.reschedule,
          record,
        })
      : null

  return prompt && { ...prompt, coupled_value: record.follow_up.written_value }
}

export function toAppointmentMockHistoryEntry({
  action,
  actor,
  link = null,
  payload,
}) {
  return {
    action,
    actor: actor ?? null,
    at: new Date().toISOString(),
    id: getAppointmentMockId(),
    link,
    payload,
  }
}

export function toAppointmentMockRescheduledEntry({
  actor,
  nextRecord,
  record,
}) {
  return toAppointmentMockHistoryEntry({
    action: AppointmentHistoryAction.rescheduled,
    actor,
    payload: {
      from_end_at: record.end_at,
      from_start_at: record.start_at,
      to_end_at: nextRecord.end_at,
      to_start_at: nextRecord.start_at,
    },
  })
}

export function toAppointmentMockDueDateEntry({ from, record, to }) {
  const { request } = record.anchor

  return toAppointmentMockHistoryEntry({
    action: AppointmentHistoryAction.updateDueDate,
    actor: record.organizer,
    link: {
      id: request.id,
      kind: AppointmentHistoryLinkKind.request,
      url: request.url ?? null,
    },
    payload: { appointment_id: record.numericId, from, to },
  })
}

export function toAppointmentMockDetail({
  historyOffset = APPOINTMENT_HISTORY_FIRST_OFFSET,
  record,
}) {
  const detail = omit(record, APPOINTMENT_MOCK_INTERNAL_FIELDS)

  const { request } = detail.anchor

  return cloneDeep({
    ...detail,
    anchor: {
      ...detail.anchor,
      request: request && { ...request, due_date: record.requestDueDate },
    },
    follow_up: {
      ...detail.follow_up,
      is_live: getIsAppointmentMockFollowUpLive(record),
    },
    follow_up_prompt: getAppointmentMockFollowUpPrompt({
      kind: AppointmentFollowUpPromptKind.create,
      record,
    }),
    history: record.history.slice(
      historyOffset,
      historyOffset + APPOINTMENT_HISTORY_PAGE_SIZE
    ),
    history_total: record.history.length,
  })
}
